import { useDynamicObjectList } from '@konova/ui/use-dynamic-object-list';
import { createContext, FC, PropsWithChildren, useContext, useMemo } from 'react';
import { RouterPrompt } from '../../components';

export type DirtyGroupEntry = {
  id: string;
  dirty: boolean;
};

export type DirtyContextType = {
  isDirty: boolean;
  removeGroup: (group: string) => void;
  addGroup: (group: string, isDirty: boolean) => void;
  reset: () => void;
};

export const DirtyContext = createContext<DirtyContextType>({
  isDirty: false,
  removeGroup: () => { },
  addGroup: () => { },
  reset: () => { }
});

export const useDirtyContext = () => useContext(DirtyContext);

/**
 * DirtyProvider is a React functional component that provides a context for managing the "dirty" state of various groups within an application. It allows child components to register themselves as "dirty" or "clean," enabling the application to track unsaved changes and prompt users before navigating away from a page with unsaved data.
  */
export const DirtyProvider: FC<PropsWithChildren & { useRouterBlocker?: boolean }> = ({ children, useRouterBlocker = true }) => {
  const list = useDynamicObjectList<DirtyGroupEntry, string>([]);
  const isDirty = useMemo(() => list.list.some(entry => entry.dirty), [list.list]);

  return (
    <DirtyContext.Provider
      value={{
        isDirty,
        addGroup: (group, dirty) => {
          list.add({ id: group, dirty });
        },
        removeGroup: list.remove,
        reset: () => list.set([])
      }}
    >
      {useRouterBlocker && <RouterPrompt when={isDirty} />}
      {children}
    </DirtyContext.Provider>
  );
};